(() => {
  if (customElements.get('vida-pura-purchase-options')) return;

  class VidaPuraPurchaseOptions extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const dataNode = this.querySelector('[data-vp-plan-data]');
      if (!dataNode) return;

      try {
        this.planData = JSON.parse(dataNode.textContent);
      } catch (error) {
        console.error('[Vida Pura purchase options] Invalid selling-plan data.', error);
        return;
      }

      this.productInfo = this.closest('product-info') || this.closest('.product__info-container') || document;
      this.infoContainer = this.closest('.product__info-container');
      this.formId = this.dataset.productFormId;
      this.sectionId = this.dataset.sectionId;
      this.defaultPurchase = this.dataset.defaultPurchase || 'subscription';
      this.selectedPurchase = this.defaultPurchase;
      this.selectedFrequency = 3;
      this.currentVariant = null;
      this.currentPlanMap = new Map();
      this.nativeObserver = null;

      this.purchaseRadios = Array.from(this.querySelectorAll('[data-vp-purchase-radio]'));
      this.frequencyInputs = Array.from(this.querySelectorAll('[data-vp-frequency-input]'));
      this.cards = Array.from(this.querySelectorAll('[data-vp-card]'));

      this.bindEvents();
      this.observeVariantInputs();
      this.updateForCurrentVariant();
    }

    disconnectedCallback() {
      this.nativeObserver?.disconnect();
    }

    bindEvents() {
      this.purchaseRadios.forEach((radio) => {
        radio.addEventListener('change', () => {
          if (!radio.checked) return;
          this.selectedPurchase = radio.value;
          this.renderSelection();
          this.syncSellingPlan();
        });
      });

      this.frequencyInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked || input.disabled) return;
          this.selectedFrequency = Number(input.value);
          this.renderPlanPrice();
          this.syncSellingPlan();
        });
      });

      this.cards.forEach((card) => {
        card.addEventListener('click', (event) => {
          if (event.target.closest('input, label, button, a, select')) return;
          const type = card.dataset.vpCard;
          const radio = this.purchaseRadios.find((item) => item.value === type);
          if (!radio || radio.checked) return;
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }

    observeVariantInputs() {
      const selector = this.getFormSelectors()
        .map((id) => `#${this.escapeSelector(id)} input[name="id"]`)
        .join(',');

      if (!selector) return;
      document.querySelectorAll(selector).forEach((input) => {
        input.addEventListener('change', () => this.updateForCurrentVariant());
      });
    }

    getFormSelectors() {
      const ids = [this.formId, `${this.formId}--alt`].filter(Boolean);
      return [...new Set(ids)];
    }

    escapeSelector(value) {
      return window.CSS?.escape ? window.CSS.escape(value) : value.replace(/([ #;?%&,.+*~\\':"!^$\[\]()=>|/@])/g, '\\$1');
    }

    getPrimaryForm() {
      return document.getElementById(this.formId);
    }

    getCurrentVariantId() {
      const form = this.getPrimaryForm();
      return form?.querySelector('input[name="id"]')?.value || null;
    }

    updateForCurrentVariant() {
      const variantId = this.getCurrentVariantId();
      const variant = variantId ? this.planData.variants?.[String(variantId)] : null;
      this.currentVariant = variant || null;

      if (!variant || !Array.isArray(variant.plans) || variant.plans.length === 0) {
        this.disableEnhancement();
        return;
      }

      this.currentPlanMap = this.buildFrequencyMap(variant.plans);
      if (this.currentPlanMap.size === 0) {
        this.disableEnhancement();
        return;
      }

      this.enableEnhancement();
      this.configureFrequencyOptions();

      if (!this.currentPlanMap.has(this.selectedFrequency)) {
        this.selectedFrequency = this.currentPlanMap.has(3)
          ? 3
          : [...this.currentPlanMap.keys()].sort((a, b) => a - b)[0];
      }

      if (this.selectedPurchase === 'subscription' && !this.currentPlanMap.has(this.selectedFrequency)) {
        this.selectedPurchase = 'one_time';
      }

      this.renderPrices();
      this.renderSelection();
      this.syncSellingPlan();
    }

    buildFrequencyMap(plans) {
      const map = new Map();
      plans.forEach((plan) => {
        const months = this.extractMonths(plan);
        if ([3, 4].includes(months) && !map.has(months)) map.set(months, plan);
      });
      return map;
    }

    extractMonths(plan) {
      const interval = String(plan.recurringInterval || '').toLowerCase();
      const count = Number(plan.recurringIntervalCount || 0);
      if (interval === 'month' && [3, 4].includes(count)) return count;

      const optionText = Array.isArray(plan.options)
        ? plan.options.map((option) => `${option.name || ''} ${option.value || ''}`).join(' ')
        : '';
      const haystack = `${plan.name || ''} ${plan.groupName || ''} ${optionText}`.toLowerCase();

      const directMatch = haystack.match(/(?:every|each|per|delivery|deliver(?:ed|y)?)?\s*(3|4)\s*months?/i);
      if (directMatch) return Number(directMatch[1]);

      const wordMap = { three: 3, four: 4 };
      const wordMatch = haystack.match(/\b(three|four)\s*months?\b/i);
      return wordMatch ? wordMap[wordMatch[1].toLowerCase()] : 0;
    }

    enableEnhancement() {
      this.hidden = false;
      this.infoContainer?.classList.add('vp-seal-enhanced');
      this.disableNativeSellingPlanControls();
      this.startNativeObserver();
    }

    disableEnhancement() {
      this.hidden = true;
      this.infoContainer?.classList.remove('vp-seal-enhanced');
      this.nativeObserver?.disconnect();
      this.nativeObserver = null;
      this.restoreNativeSellingPlanControls();
      this.removeCustomSellingPlanInputs();
    }

    startNativeObserver() {
      if (this.nativeObserver || !this.infoContainer) return;
      this.nativeObserver = new MutationObserver(() => this.disableNativeSellingPlanControls());
      this.nativeObserver.observe(this.infoContainer, { childList: true, subtree: true });
    }

    disableNativeSellingPlanControls() {
      const scope = this.infoContainer || document;
      scope.querySelectorAll('[name="selling_plan"]:not([data-vp-selling-plan])').forEach((control) => {
        if (typeof control.dataset.vpWasDisabled === 'undefined') {
          control.dataset.vpWasDisabled = control.disabled ? 'true' : 'false';
        }
        control.disabled = true;
      });
    }

    restoreNativeSellingPlanControls() {
      const scope = this.infoContainer || document;
      scope.querySelectorAll('[name="selling_plan"]:not([data-vp-selling-plan])').forEach((control) => {
        if (control.dataset.vpWasDisabled === 'false') control.disabled = false;
        delete control.dataset.vpWasDisabled;
      });
    }

    configureFrequencyOptions() {
      const missing = [];
      this.frequencyInputs.forEach((input) => {
        const month = Number(input.value);
        const isAvailable = this.currentPlanMap.has(month);
        input.disabled = !isAvailable;
        input.checked = isAvailable && month === this.selectedFrequency;
        const label = this.querySelector(`[data-vp-frequency-label="${month}"]`);
        if (label) {
          label.title = isAvailable ? '' : `The ${month}-month selling plan must be created in Seal Subscriptions.`;
          label.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
        }
        if (!isAvailable) missing.push(month);
      });

      const message = this.querySelector('[data-vp-frequency-message]');
      if (!message) return;
      if (missing.length) {
        message.hidden = false;
        message.textContent = `Some refill schedules are temporarily unavailable (${missing.join(' and ')} months).`;
      } else {
        message.hidden = true;
        message.textContent = '';
      }
    }

    renderPrices() {
      if (!this.currentVariant) return;
      const oneTimePrice = Number(this.currentVariant.price || 0);
      const oneTimeCompare = Number(this.currentVariant.compareAtPrice || 0);

      this.setMoney('[data-vp-one-time-price]', oneTimePrice);
      this.setCompareMoney('[data-vp-one-time-compare]', oneTimeCompare > oneTimePrice ? oneTimeCompare : 0);
      this.renderPlanPrice();
    }

    renderPlanPrice() {
      const plan = this.currentPlanMap.get(this.selectedFrequency);
      if (!plan || !this.currentVariant) return;

      const oneTimePrice = Number(this.currentVariant.price || 0);
      const planPrice = Number(plan.price || 0);
      const comparePrice = Math.max(Number(plan.compareAtPrice || 0), oneTimePrice);
      const saving = Math.max(0, comparePrice - planPrice);
      const recurringPrice = this.getRecurringPrice(plan, oneTimePrice);

      this.setMoney('[data-vp-subscription-price]', planPrice);
      this.setCompareMoney('[data-vp-subscription-compare]', comparePrice > planPrice ? comparePrice : 0);

      const savingNode = this.querySelector('[data-vp-saving]');
      if (savingNode) {
        savingNode.hidden = saving <= 0;
        savingNode.textContent = saving > 0 ? `Save ${this.formatMoney(saving)}` : '';
      }

      const refillNode = this.querySelector('[data-vp-refill-summary]');
      if (refillNode) {
        refillNode.textContent = `Replacement cartridge delivered every ${this.selectedFrequency} months for ${this.formatMoney(recurringPrice)}.`;
      }
    }

    getRecurringPrice(plan, basePrice) {
      const adjustments = Array.isArray(plan.priceAdjustments) ? plan.priceAdjustments : [];
      if (adjustments.length > 1) return this.applyAdjustment(basePrice, adjustments[adjustments.length - 1]);
      return Number(plan.perDeliveryPrice || plan.price || basePrice);
    }

    applyAdjustment(basePrice, adjustment) {
      const value = Number(adjustment?.value || 0);
      const type = String(adjustment?.valueType || '').toLowerCase();
      if (type === 'percentage') return Math.max(0, Math.round(basePrice * (100 - value) / 100));
      if (type === 'fixed_amount') return Math.max(0, basePrice - value);
      if (type === 'price') return Math.max(0, value);
      return basePrice;
    }

    renderSelection() {
      this.purchaseRadios.forEach((radio) => {
        radio.checked = radio.value === this.selectedPurchase;
      });

      this.cards.forEach((card) => {
        const selected = card.dataset.vpCard === this.selectedPurchase;
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-expanded', selected ? 'true' : 'false');
      });

      const subscriptionDetails = this.querySelector('[data-vp-subscription-details]');
      const oneTimeDetails = this.querySelector('[data-vp-one-time-details]');
      if (subscriptionDetails) subscriptionDetails.hidden = this.selectedPurchase !== 'subscription';
      if (oneTimeDetails) oneTimeDetails.hidden = this.selectedPurchase !== 'one_time';

      this.frequencyInputs.forEach((input) => {
        input.checked = !input.disabled && Number(input.value) === this.selectedFrequency;
      });
    }

    syncSellingPlan() {
      if (!this.currentVariant) return;
      const plan = this.selectedPurchase === 'subscription'
        ? this.currentPlanMap.get(this.selectedFrequency)
        : null;
      const planId = plan ? String(plan.id) : '';

      this.getFormSelectors().forEach((formId) => {
        const form = document.getElementById(formId);
        if (!form) return;

        let input = form.querySelector('input[data-vp-selling-plan]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selling_plan';
          input.dataset.vpSellingPlan = 'true';
          form.appendChild(input);
        }

        input.value = planId;
        input.disabled = !planId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      this.disableNativeSellingPlanControls();
    }

    removeCustomSellingPlanInputs() {
      this.getFormSelectors().forEach((formId) => {
        document.getElementById(formId)?.querySelectorAll('[data-vp-selling-plan]').forEach((input) => input.remove());
      });
    }

    setMoney(selector, cents) {
      const node = this.querySelector(selector);
      if (node) node.textContent = this.formatMoney(cents);
    }

    setCompareMoney(selector, cents) {
      const node = this.querySelector(selector);
      if (!node) return;
      node.hidden = !cents;
      node.textContent = cents ? this.formatMoney(cents) : '';
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const currency = this.dataset.currencyCode || this.planData.currencyCode || 'AUD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
          style: 'currency',
          currency,
          minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
          maximumFractionDigits: 2
        }).format(amount);
      } catch (_error) {
        return `$${amount.toFixed(2)}`;
      }
    }
  }

  customElements.define('vida-pura-purchase-options', VidaPuraPurchaseOptions);
})();
